import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { IconDotsVertical, IconPencil, IconTrash } from "@tabler/icons-react";

import { api, ApiError } from "../lib/api";

interface RowActionsMenuProps {
  /**
   * API path for DELETE, e.g. "/v1/divisions/uuid-here".
   * The same list of query keys is invalidated on success.
   */
  deletePath: string;
  invalidateKeys: string[];
  entityLabel: string;
  onEdit: () => void;
}

/**
 * Compact edit / delete dropdown for tree rows. Edit calls the provided
 * onEdit handler (parent opens the correct form modal); delete confirms
 * inline and POSTs to the API, invalidating listed query keys.
 *
 * If the API returns 409 CONFLICT (foreign key restrict), we surface the
 * message so the user knows they need to clear children first.
 */
export function RowActionsMenu({
  deletePath,
  invalidateKeys,
  entityLabel,
  onEdit,
}: RowActionsMenuProps) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) {
        setOpen(false);
        setConfirming(false);
      }
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  const delMut = useMutation({
    mutationFn: () => api.del(deletePath),
    onSuccess: () => {
      for (const key of invalidateKeys) {
        qc.invalidateQueries({ queryKey: [key] });
      }
      setOpen(false);
      setConfirming(false);
      setError(null);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : String(err));
    },
  });

  return (
    <div className="relative inline-flex" ref={ref}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
          setConfirming(false);
          setError(null);
        }}
        className="rounded p-1 text-text-secondary hover:bg-surface-1 hover:text-text-primary"
        title="More actions"
        aria-label={`Actions for ${entityLabel}`}
      >
        <IconDotsVertical size={14} />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-20 mt-1 w-40 rounded border border-border bg-surface-2 shadow-lg">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              onEdit();
            }}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-surface-1"
          >
            <IconPencil size={12} /> Edit
          </button>
          {!confirming ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setConfirming(true);
              }}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-red-700 hover:bg-red-50"
            >
              <IconTrash size={12} /> Delete
            </button>
          ) : (
            <div className="border-t border-border p-2 text-xs">
              <div className="mb-1 text-text-secondary">
                Delete {entityLabel}?
              </div>
              <div className="flex gap-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    delMut.mutate();
                  }}
                  disabled={delMut.isPending}
                  className="flex-1 rounded bg-red-600 px-2 py-1 text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {delMut.isPending ? "…" : "Delete"}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirming(false);
                  }}
                  className="flex-1 rounded border border-border px-2 py-1 hover:bg-surface-1"
                >
                  Cancel
                </button>
              </div>
              {error && (
                <div className="mt-1 text-[10px] text-red-600">{error}</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
